import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { fetchDashboardStats } from "../services/api";
import {
  ArrowLeft,
  ArrowRight,
  AlertCircle,
  BarChart3,
  DollarSign,
  Package,
  RefreshCw,
  ShoppingBag,
  TrendingDown,
  TrendingUp,
  Truck,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from "recharts";

// Revenue, profit, best sellers and supplier spending — moved off the Home
// page so the landing page stays a launcher. Data: /api/dashboard/stats/.

const SectionHeading = ({ icon: Icon, tone, title }) => (
  <div className="flex items-center gap-3 mb-4">
    <div className={`p-2 rounded-lg ${tone}`}>
      <Icon size={22} />
    </div>
    <h2 className="text-2xl font-bold text-gray-800">{title}</h2>
  </div>
);

const formatLKR = (amount) =>
  new Intl.NumberFormat("en-LK", { style: "currency", currency: "LKR" }).format(amount || 0);

const EMPTY_STATS = {
  total_inventory_value: 0,
  total_sales: 0,
  total_profit: 0,
  supplier_stats: [],
  top_sold_parts: [],
  sales_trend: [],
};

const InsightsPage = () => {
  const [stats, setStats] = useState(EMPTY_STATS);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState("all");

  const load = useCallback(async (p) => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchDashboardStats(p);
      setStats({ ...EMPTY_STATS, ...(data || {}) });
    } catch (err) {
      console.error("Failed to load dashboard stats", err);
      setError("Could not load business insights.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load(period);
  }, [period, load]);

  return (
    <div className="min-h-screen bg-gray-50 pb-12">
      <div className="max-w-7xl mx-auto p-4 md:p-8">
        {/* Header */}
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6 mb-8">
          <div className="flex items-center gap-4">
            <Link
              to="/"
              className="p-2 bg-white rounded-full text-gray-400 hover:text-gray-800 hover:bg-gray-100 transition-colors shadow-sm shrink-0"
            >
              <ArrowLeft size={24} />
            </Link>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-gray-800 leading-tight">Business Insights</h1>
              <p className="text-sm md:text-base text-gray-500">Revenue & profit, best sellers and supplier spending</p>
            </div>
          </div>
          <select
            className="p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 font-medium text-gray-700 bg-white cursor-pointer shadow-sm"
            value={period}
            onChange={(e) => setPeriod(e.target.value)}
          >
            <option value="today">Today</option>
            <option value="this_week">This Week</option>
            <option value="this_month">This Month</option>
            <option value="all">All Time</option>
          </select>
        </div>

        {loading ? (
          <div className="p-16 text-center text-gray-500 bg-white rounded-2xl shadow-sm border border-gray-100 animate-pulse">
            <BarChart3 className="mx-auto mb-4 opacity-20" size={48} />
            <p>Gathering latest financial data...</p>
          </div>
        ) : error ? (
          <div className="p-12 text-center bg-white rounded-2xl border border-red-200">
            <p className="text-red-600 font-medium mb-4">{error}</p>
            <button
              onClick={() => load(period)}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-red-600 text-white font-bold hover:bg-red-700"
            >
              <RefreshCw size={16} /> Try again
            </button>
          </div>
        ) : (
          <>
            {/* --- SECTION 1: Financial Overview --- */}
            <SectionHeading icon={BarChart3} tone="bg-red-100 text-red-700" title="Financial Overview" />
            {/* Stats Cards Row */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-10">
              {/* 1. Inventory Cost Card */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-500 font-bold uppercase text-xs tracking-wider">
                    Total Stock Value
                  </h3>
                  <div className="p-2 bg-blue-50 rounded-lg text-blue-500">
                    <ShoppingBag size={20} />
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-gray-900">
                  {formatLKR(stats.total_inventory_value)}
                </p>
                <div className="mt-3 flex items-center text-xs text-gray-400">
                   <span className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-2"></span> Cost of unsold parts
                </div>
              </div>

              {/* 2. Total Sales Card */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
                <div className="flex items-center justify-between mb-2">
                  <h3 className="text-gray-500 font-bold uppercase text-xs tracking-wider">
                    Total Revenue
                  </h3>
                  <div className="p-2 bg-orange-50 rounded-lg text-orange-500">
                     <DollarSign size={20} />
                  </div>
                </div>
                <p className="text-3xl font-extrabold text-gray-900">
                  {formatLKR(stats.total_sales)}
                </p>
                 <div className="mt-3 flex items-center text-xs text-gray-400">
                   <span className="w-1.5 h-1.5 bg-orange-400 rounded-full mr-2"></span> Total income from sales
                </div>
              </div>

              {/* 3. Net Profit / Loss Card */}
              <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow relative overflow-hidden">
                <div className={`absolute top-0 right-0 w-20 h-20 ${stats.total_profit >= 0 ? 'bg-green-50' : 'bg-red-50'} rounded-bl-full -mr-6 -mt-6`}></div>
                <div className="flex items-center justify-between mb-2 relative z-10">
                  <h3 className="text-gray-500 font-bold uppercase text-xs tracking-wider">
                    {stats.total_profit >= 0 ? 'Net Profit' : 'Net Loss'}
                  </h3>
                  <div className={`p-2 ${stats.total_profit >= 0 ? 'bg-green-50 text-green-500' : 'bg-red-50 text-red-500'} rounded-lg`}>
                     {stats.total_profit >= 0 ? <TrendingUp size={20} /> : <TrendingDown size={20} />}
                  </div>
                </div>
                <p className={`text-3xl font-extrabold ${stats.total_profit >= 0 ? 'text-green-600' : 'text-red-600'} relative z-10`}>
                  {formatLKR(stats.total_profit)}
                </p>
                <div className="mt-3 flex items-center text-xs text-gray-400 relative z-10">
                   <span className="w-1.5 h-1.5 bg-green-400 rounded-full mr-2"></span> Revenue - Purchase Cost
                </div>
              </div>
            </div>

            {/* --- Sales Trend Chart --- */}
            <div className="bg-white p-6 rounded-2xl shadow-sm border border-gray-100 mb-10">
              <h3 className="text-lg font-bold text-gray-800 mb-6">Revenue Trend</h3>
              <div className="h-72 w-full">
                {stats.sales_trend && stats.sales_trend.length > 0 ? (
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={stats.sales_trend}>
                      <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                      <XAxis dataKey="date" axisLine={false} tickLine={false} tick={{fill: '#6B7280', fontSize: 12}} dy={10} />
                      <YAxis axisLine={false} tickLine={false} tickFormatter={(value) => `Rs ${value}`} tick={{fill: '#6B7280', fontSize: 12}} />
                      <Tooltip formatter={(value) => formatLKR(value)} cursor={{fill: '#fef2f2'}} contentStyle={{borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'}} />
                      <Legend verticalAlign="top" height={36} iconType="circle" />
                      <Bar dataKey="revenue" name="Revenue" fill="#ef4444" radius={[4, 4, 0, 0]} maxBarSize={40} />
                      <Bar dataKey="profit" name="Profit" fill="#10b981" radius={[4, 4, 0, 0]} maxBarSize={40} />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="flex flex-col items-center justify-center h-full text-gray-400 gap-3">
                    <BarChart3 size={32} opacity={0.2} />
                    <p>No sales data to display for this period.</p>
                  </div>
                )}
              </div>
            </div>

            {/* --- SECTION 2: Best Selling Parts --- */}
            <SectionHeading icon={TrendingUp} tone="bg-orange-100 text-orange-700" title="Best Selling Parts" />
            {stats.top_sold_parts && stats.top_sold_parts.length > 0 ? (
              <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200 mb-10">
                <div className="p-6 border-b border-gray-100 flex items-center justify-between bg-gray-50/50">
                  <div className="flex items-center gap-3">
                    <div className="p-2 w-10 h-10 flex items-center justify-center bg-white border border-gray-200 rounded-lg shadow-sm">
                      <TrendingUp className="text-orange-600" size={20} />
                    </div>
                    <h2 className="text-xl font-bold text-gray-800">Top Selling Parts</h2>
                  </div>
                </div>
                <div className="p-6 overflow-x-auto pb-8">
                  <div className="flex gap-6 pb-2">
                    {stats.top_sold_parts.map((part, index) => (
                      <Link 
                        to="/inventory"
                        key={part.id || index} 
                        className="min-w-[240px] max-w-[280px] bg-white border border-gray-100 rounded-xl p-4 shadow-sm hover:shadow-md transition-shadow group flex-shrink-0 block"
                      >
                        <div className="flex justify-between items-start mb-4">
                          <span className="bg-orange-100 text-orange-800 text-xs font-bold px-2.5 py-1 rounded-full flex items-center gap-1 border border-orange-200 shadow-sm">
                            <TrendingUp size={12} /> #{index + 1} Best Seller
                          </span>
                        </div>
                        <div className="h-40 bg-gray-50 rounded-lg mb-4 overflow-hidden flex items-center justify-center relative border border-gray-100">
                           {part.image ? (
                             <img src={part.image} alt={part.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" />
                           ) : (
                             <Package size={32} className="text-gray-300" />
                           )}
                        </div>
                        <h3 className="font-bold text-gray-800 truncate mb-1" title={part.name}>{part.name}</h3>
                        <p className="text-xs text-gray-500 font-mono mb-4">{part.part_number}</p>
                        <div className="flex justify-between items-end border-t border-gray-100 pt-4">
                           <div>
                             <p className="text-[10px] text-gray-400 font-semibold mb-0.5 uppercase tracking-wider">Total Sold</p>
                             <p className="text-xl font-black text-gray-800">{part.total_sold} <span className="text-xs font-medium text-gray-500">Units</span></p>
                           </div>
                           <div className="w-8 h-8 rounded-full bg-gray-50 flex items-center justify-center text-gray-400 group-hover:bg-red-50 group-hover:text-red-600 transition-colors shadow-sm border border-gray-100">
                             <ArrowRight size={16} />
                           </div>
                        </div>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            ) : (
              <div className="bg-white p-12 rounded-2xl border border-gray-200 text-center text-gray-400 mb-10">
                No parts sold in this period yet.
              </div>
            )}

            {/* --- SECTION 3: Spending per Supplier --- */}
            <SectionHeading icon={Truck} tone="bg-blue-100 text-blue-700" title="Spending per Supplier" />
            <div className="bg-white rounded-2xl shadow-sm overflow-hidden border border-gray-200">
              <div className="p-6 border-b border-gray-100 flex items-center gap-3 bg-gray-50/50">
                <div className="p-2 bg-white border border-gray-200 rounded-lg shadow-sm">
                   <Truck className="text-red-700" size={18} />
                </div>
                <h2 className="text-lg font-bold text-gray-800">
                  Spending per Supplier
                </h2>
              </div>

              {/* Empty State Check */}
              {stats.supplier_stats.length === 0 ? (
                <div className="p-12 text-center text-gray-400 flex flex-col items-center justify-center gap-3">
                  <div className="p-4 bg-gray-50 rounded-full">
                     <AlertCircle size={32} opacity={0.5} />
                  </div>
                  <span>
                    No supplier data available yet. Add stock to see stats.
                  </span>
                </div>
              ) : (
                <>
                  {/* MOBILE VIEW (Card List) */}
                  <div className="block md:hidden">
                    <div className="divide-y divide-gray-100">
                      {stats.supplier_stats.map((sup, index) => (
                        <div key={index} className="p-5 flex flex-col gap-3 hover:bg-gray-50 transition-colors">
                          <div className="flex justify-between items-start">
                            <div className="flex gap-3 items-center">
                               <div className="w-8 h-8 rounded-full bg-red-100 text-red-600 flex items-center justify-center text-xs font-bold">
                                  {(sup.supplier__name || "U").charAt(0)}
                               </div>
                               <div>
                                  <p className="text-xs text-gray-500 uppercase font-bold mb-0.5">
                                    Supplier
                                  </p>
                                  <p className="font-bold text-gray-800 text-lg leading-tight">
                                    {sup.supplier__name || "Unknown"}
                                  </p>
                               </div>
                            </div>
                            <span className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-xs font-bold border border-blue-100 whitespace-nowrap">
                              {sup.part_count} Units
                            </span>
                          </div>
                          <div className="flex justify-between items-center bg-gray-50 p-3 rounded-lg mt-1">
                            <p className="text-xs text-gray-500 font-medium">
                              Total Investment
                            </p>
                            <p className="text-lg font-bold text-gray-800">
                              {formatLKR(sup.total_spent)}
                            </p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* DESKTOP VIEW (Table) */}
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-left">
                      <thead className="bg-gray-50/80 text-gray-500 text-xs uppercase font-bold tracking-wider border-b border-gray-100">
                        <tr>
                          <th className="p-5 pl-8">Supplier Name</th>
                          <th className="p-5 text-center">Current Stock</th>
                          <th className="p-5 text-right pr-8">Investment Value</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-50">
                        {stats.supplier_stats.map((sup, index) => (
                          <tr
                            key={index}
                            className="hover:bg-gray-50 transition duration-150 group"
                          >
                            <td className="p-5 pl-8 font-medium text-gray-800 flex items-center gap-3">
                               <div className="w-8 h-8 rounded-lg bg-gray-100 text-gray-500 group-hover:bg-red-100 group-hover:text-red-600 transition-colors flex items-center justify-center text-xs font-bold">
                                  {(sup.supplier__name || "U").charAt(0)}
                               </div>
                               {sup.supplier__name || "Unknown"}
                            </td>
                            <td className="p-5 text-center">
                              <span className="bg-white text-gray-600 px-3 py-1 rounded-full text-xs font-bold border border-gray-200 shadow-sm">
                                {sup.part_count} units
                              </span>
                            </td>
                            <td className="p-5 text-right font-bold text-gray-700 pr-8">
                              {formatLKR(sup.total_spent)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default InsightsPage;
